// File: components/shared/Modals.jsx
import React, { useState } from 'react';
import { DownloadIcon, CloseIcon, SpinnerIcon, FileIcon, UserIcon } from './Icons';
import { formatCurrency, parseCurrency, formatDateForDisplay } from '../../utils/format';

/* ── Status color helpers ── */
const getAbnStatusColor = (s) => {
  if (s === 'Paid') return 'fp-badge-green';
  if (s === 'Waiting For Invoice') return 'fp-badge-amber';
  return 'fp-badge-blue';
};

const getTfnStatusColor = (s) => {
  if (s === 'Paid') return 'fp-badge-green';
  return 'fp-badge-red';
};

/* ── Shared sub-components ── */
const ModalField = ({ label, value, mono }) => (
  <div className="fp-modal-field">
    <p className="fp-modal-field-label">{label}</p>
    <p className={`fp-modal-field-value ${mono ? 'fp-mono' : ''}`}>{value || '—'}</p>
  </div>
);

const AmountField = ({ label, value, colorClass, large }) => (
  <div className="fp-modal-field">
    <p className="fp-modal-field-label">{label}</p>
    <p className={`fp-modal-amount ${colorClass} ${large ? 'fp-modal-amount-lg' : ''} fp-mono`}>
      {value}
    </p>
  </div>
);

const LeaveChip = ({ label, value }) => (
  <div className="fp-leave-chip">
    <p className="fp-leave-chip-label">{label}</p>
    <p className="fp-leave-chip-value">{value}</p>
  </div>
);

/* ─────────────────────────────────────────────
   ABN MODAL - View / Create Invoice
   ───────────────────────────────────────────── */
export const ABNModal = ({ data, onClose, onCreateInvoice, refreshPayments }) => {
  const [creating, setCreating] = useState(false);
  const canCreate = data[3] === 'Waiting For Invoice';
  const statusClass = getAbnStatusColor(data[3]);

  const handleCreate = async () => {
  setCreating(true);
  try {
    const response = await fetch(`/api/create-invoice/${data[0]}`);
    const result = await response.json();
    if (result.success) {
      window.open(`/api/download-invoice/${data[0]}`, '_blank');
      onClose();
      if (refreshPayments) {
        setTimeout(() => refreshPayments(), 5000);
      }
    }
  } catch (err) {
    console.error('Error creating invoice:', err);
  } finally {
    setCreating(false);
  }
};

  const handleDownload = () => {
    window.open(`/api/download-invoice/${data[0]}`, '_blank');
  };





  /* Loading state */
  if (creating) {
    return (
      <div className="fp-overlay">
        <div className="fp-modal-card">
          <div className="fp-modal-loading">
            <SpinnerIcon />
            <p className="fp-modal-loading-title">Generating Invoice…</p>
            <p className="fp-modal-loading-msg">Please wait</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fp-overlay" onClick={onClose}>
      <div className="fp-modal-card" onClick={e => e.stopPropagation()}>
        <div className="fp-modal-gradient-bar" />

        {/* Header */}
        <div className="fp-modal-header">
          <div className="fp-modal-header-left">
            <div className="fp-modal-icon fp-modal-icon-cyan">
              <FileIcon />
            </div>
            <span className="fp-modal-title">
              {canCreate ? 'Tax Invoice' : 'Invoice Summary'}
            </span>
          </div>
          <button className="fp-modal-close-x" onClick={onClose}>
            <CloseIcon />
          </button>
        </div>

        {/* Body */}
        <div className="fp-modal-body">
          {/* Status + Date row */}
          <div className="fp-modal-status-row">
            <span className={`fp-badge ${statusClass}`}>{data[3]}</span>
            <span className="fp-modal-date">{formatDateForDisplay(data[2])}</span>
          </div>

          {/* Business & PO */}
          <div className="fp-modal-row">
            <ModalField label="Business" value={data[15]} />
            <ModalField label="PO Number" value={data[4]} />
          </div>

          {/* Amounts */}
          <div className="fp-modal-amount-box fp-modal-amount-box-cyan">
            <div className="fp-modal-row">
              <AmountField label="Amount" value={formatCurrency(parseCurrency(data[5]))} colorClass="fp-color-cyan" />
              <AmountField label="GST" value={formatCurrency(parseCurrency(data[6]))} colorClass="fp-color-amber" />
            </div>
            <AmountField label="Total (inc. GST)" value={formatCurrency(parseCurrency(data[7]))} colorClass="fp-color-green" large />
          </div>

          {/* Invoice & ABN */}
          {data[16] && (
            <div className="fp-modal-row">
              <ModalField label="Invoice #" value={data[16]} />
              <ModalField label="ABN" value={data[17]} mono />
            </div>
          )}

          {/* Banking Details */}
          <div className="fp-modal-section-divider">
            <p className="fp-modal-section-title">Banking Details</p>
            <ModalField label="Account Name" value={data[8]} />
            <div className="fp-modal-row fp-modal-row-mt">
              <ModalField label="BSB" value={data[9]} mono />
              <ModalField label="Account #" value={data[10]} mono />
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="fp-modal-footer">
          <button className="fp-btn-modal-close" onClick={onClose}>
            <CloseIcon /> Close
          </button>
          {canCreate ? (
            <button className="fp-btn-modal-create" onClick={handleCreate}>
              <FileIcon /> Create Invoice
            </button>
          ) : (
            <button className="fp-btn-modal-download" onClick={handleDownload}>
              <DownloadIcon /> Invoice
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

/* ─────────────────────────────────────────────
   TFN MODAL - Employee Payslip View
   ───────────────────────────────────────────── */
export const TFNModal = ({ data, onClose, onToast }) => {
  const statusClass = getTfnStatusColor(data[17]);
  const handleDownloadPayslip = () => {
    const checkDate = new Date('2025-07-01');
    const payslipDate = new Date(data[2]);
    if (payslipDate < checkDate ) {
      onToast({ message: 'Payslips are only available after 01/07/2025.', type: 'warning' });

    } else {

      window.open(`/api/download-payslip/${data[0]}`, '_blank');
    }
    
  };
  return (
    <div className="fp-overlay" onClick={onClose}>
      <div className="fp-modal-card" onClick={e => e.stopPropagation()}>
        <div className="fp-modal-gradient-bar" />

        {/* Header */}
        <div className="fp-modal-header">
          <div className="fp-modal-header-left">
            <div className="fp-modal-icon fp-modal-icon-purple">
              <UserIcon />
            </div>
            <span className="fp-modal-title">Employee Payslip</span>
          </div>
          <button className="fp-modal-close-x" onClick={onClose}>
            <CloseIcon />
          </button>
        </div>

        {/* Body */}
        <div className="fp-modal-body">
          {/* Status + Date */}
          <div className="fp-modal-status-row">
            <span className={`fp-badge ${statusClass}`}>{data[17]}</span>
            <span className="fp-modal-date">{formatDateForDisplay(data[2])}</span>
          </div>

          <ModalField label="Employee Name" value={data[16]} />

          {/* Salary */}
          <div className="fp-modal-amount-box fp-modal-amount-box-purple">
            <div className="fp-modal-row">
              <AmountField label="Gross Salary" value={formatCurrency(parseCurrency(data[3]))} colorClass="fp-color-cyan" />
              <AmountField label="Net Pay" value={formatCurrency(parseCurrency(data[4]))} colorClass="fp-color-green" />
            </div>
            <div className="fp-modal-row">
              <AmountField label="PAYG" value={formatCurrency(parseCurrency(data[5]))} colorClass="fp-color-orange" />
              <AmountField label="Super" value={formatCurrency(parseCurrency(data[6]))} colorClass="fp-color-purple" />
            </div>
            <AmountField label="Annual Salary" value={formatCurrency(parseCurrency(data[7]))} colorClass="fp-color-blue" large />
          </div>

          {/* Leave Balances */}
          <div className="fp-modal-section-divider">
            <p className="fp-modal-section-title">Leave Balances</p>
            <div className="fp-leave-row">
              <LeaveChip label="Annual" value={data[8]} />
              <LeaveChip label="Personal" value={data[9]} />
              <LeaveChip label="Long Service" value={data[10]} />
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="fp-modal-footer">
          <button className="fp-btn-modal-close" onClick={onClose}>
            <CloseIcon /> Close
          </button>
          <button className="fp-btn-modal-download" onClick={handleDownloadPayslip}>
            <DownloadIcon /> Payslip
          </button>
        </div>
      </div>
    </div>
  );
};